import { watch } from 'vue';
import { useArrayDifference } from '@vueuse/core';

import TripPlannerModel from '@apps/main/modules/tripPlannerModel/TripPlannerModel';
import type { MapState } from '@apps/main/modules/state/mapState';
import type { RecipeState } from '@apps/main/modules/state/recipeState';
import type { TripState } from '@apps/main/modules/state/tripState';

import type { HotelConstraintQL } from '@libs/trip-planner-interfaces';

import type { HotelIngredient } from '../recipe/ingredients';
import type { CriteriaState } from '../state/criteriaState';
import type { ProductDetailState } from '../state/productDetailState';
import { useStaticDataStore } from '../store/staticDataStore';
import { useEditingIngredientStore } from '../store/editingIngredientStore';
import { adaptHotelIngredientToHotelConstraintQL } from '../criteria/ingredientAdapters/activitiesInDestinationSearchAdapter/activitiesInDestinationSearchAdapter';
import { stringComparer } from '../helpers/comparisons';
import type { OwnStayIngredient } from '../recipe/ingredients/ownStayIngredient';
import { isOwnStayIngredient } from '../recipe/ingredients/typeGuards';

import { useItineraryViewModel } from './ItineraryViewModel';
import { useDestinationAvailabilityViewModel } from './DestinationAvailabilityViewModel';
import { useProductAvailabilityViewModel } from './ProductAvailabilityViewModel';
import { useRecipeViewModel } from './RecipeViewModel';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useTripPlannerViewModel(
  recipeState: RecipeState,
  tripState: TripState,
  productDetailState: ProductDetailState,
  criteriaState: CriteriaState,
  mapState: MapState
) {
  const { setSearchSuggestionResults } = recipeState;

  const { searchSuggestionCriteria, searchActivitiesInDestinationCriteria } =
    criteriaState;

  const { mapShouldCenterOnMarkers, mapViews } = mapState;

  const staticDataStore = useStaticDataStore();
  staticDataStore.setAllowMultipleVehiclesToFerryIngredients();

  const editingIngredientStore = useEditingIngredientStore();

  const model = new TripPlannerModel();

  useRecipeViewModel(recipeState, tripState, model);
  useItineraryViewModel(recipeState, tripState, criteriaState, model);
  useDestinationAvailabilityViewModel(tripState, criteriaState, model);
  useProductAvailabilityViewModel(
    tripState,
    productDetailState,
    criteriaState,
    model
  );

  watch(
    searchSuggestionCriteria,
    (criteria) => {
      if ('searchText' in criteria && criteria.searchText === '') {
        return;
      }
      model
        .searchAutoCompleteSuggestions(searchSuggestionCriteria.value)
        .subscribe((response) => {
          setSearchSuggestionResults(response);
        });
    },
    { deep: true }
  );

  watch(
    searchActivitiesInDestinationCriteria,
    (newValue, oldValue) => {
      const arrayDifference = useArrayDifference(
        newValue,
        oldValue ?? [],
        (a, b) => stringComparer(a, b) === 0
      );
      arrayDifference.value.forEach((criteria) => {
        const destination = recipeState.getStayIngredient(
          criteria.ingredientId
        );
        if (!destination) {
          return;
        }
        searchActivitiesInDestination(destination);
      });
    },
    { immediate: true, deep: true }
  );

  mapViews.subscribe((polygonConstraint) => {
    mapShouldCenterOnMarkers.value = false;

    if (!editingIngredientStore.editingStayIngredient) {
      return;
    }

    editingIngredientStore.editingStayIngredient.polygonConstraint =
      polygonConstraint;
  });

  function searchActivitiesInDestination(
    destination: HotelIngredient | OwnStayIngredient
  ): void {
    const constraint: HotelConstraintQL = isOwnStayIngredient(destination)
      ? {
          destination: destination.searchText,
          boardBasis: [],
          selectedHotelTypes: [],
        }
      : adaptHotelIngredientToHotelConstraintQL(destination);
    model.searchAreThereActivityStaticInfo(constraint).subscribe((response) => {
      tripState.destinationWithAvailableActivities.value[
        destination.ingredientId
      ] = response;
    });
  }
}

export type TripPlannerViewModel = ReturnType<typeof useTripPlannerViewModel>;
